<script setup lang="ts">
import stores from "@/data/stores.gen";
import { EntityTypeName } from "@/data/types/types.gen.d";
import { FormInputSelect, FormValues } from "../types";
import { eventBus } from "../event_bus";

const { t } = useI18n();

/////////////////////////////////////////////////////////////
//////////////////////// PROPS //////////////////////////////
/////////////////////////////////////////////////////////////
const options = ref<any[]>([]);
const props = defineProps<{
  config: FormInputSelect;
  formValues?: FormValues;
  modelValue:
    | string
    | string[]
    | { [key: string]: string }[]
    | { [key: string]: string };
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (
    e: "update:modelValue",
    value:
      | string
      | string[]
      | { [key: string]: string }[]
      | { [key: string]: string },
  ): void;
}>();

/////////////////////////////////////////////////////////////
////////////////////// VARIABLES ////////////////////////////
/////////////////////////////////////////////////////////////
const loading = ref<boolean>(false);

/////////////////////////////////////////////////////////////
////////////////////// COMPUTED /////////////////////////////
/////////////////////////////////////////////////////////////
const value = computed<
  string | string[] | { [key: string]: string }[] | { [key: string]: string }
>({
  get: () => {
    if (!props.modelValue) {
      if (props.config.defaultValue) {
        return props.config.defaultValue;
      }
      if (props.config.multiple) {
        return [];
      } else {
        return "";
      }
    }

    // Get value
    return getValue();
  },
  set: (
    value:
      | string
      | string[]
      | { [key: string]: string }[]
      | { [key: string]: string },
  ) => {
    let val = value;
    if (!Array.isArray(value)) {
      if (props.config.valueMapping) {
        val = valueToObject(value as string);
      }
    } else {
      if (props.config.valueMapping) {
        val = valueToObjectArray(value as string[]);
      }
    }

    // Emit update
    emit("update:modelValue", val);
    eventBus.emit(`form.update.${props.config.name}`, val);
  },
});

/////////////////////////////////////////////////////////////
////////////////////// METHODS //////////////////////////////
/////////////////////////////////////////////////////////////
async function getOptions() {
  if (props.config.options === undefined) return [];
  if (typeof props.config.options === "string")
    return props.formValues![props.config.options] as {
      [key: string]: string;
    }[];
  if (Array.isArray(props.config.options)) return props.config.options;
  if (props.config.optionStore) {
    const store = stores[props.config.optionStore as EntityTypeName]();
    const { items } = await store.retrieveAll();
    return await props.config.options(items);
  }
  return await props.config.options(props.formValues);
}

function getValue(): any {
  if (props.config.valueMapping) {
    if (Array.isArray(props.modelValue)) {
      return formatObjectArray(props.modelValue as { [key: string]: string }[]);
    } else {
      return formatObject(props.modelValue as { [key: string]: string });
    }
  } else {
    if (props.modelValue) {
      return props.modelValue;
    }
    return props.config.defaultValue ?? props.modelValue;
  }
}

function formatObjectArray(objArr: { [key: string]: string }[]): string[] {
  return objArr.map((v) => formatObject(v));
}

function formatObject(obj: { [key: string]: string }): string {
  return obj[props.config.valueMapping?.value ?? "value"];
}

function valueToObjectArray(valArr: string[]): { [key: string]: string }[] {
  return valArr.map((v) => valueToObject(v));
}

function valueToObject(val: string): { [key: string]: string } {
  const option = options.value.find((option) => option.value === val);
  if (!option) return {};
  return {
    [props.config.valueMapping?.title ?? "title"]: option.title,
    [props.config.valueMapping?.value ?? "value"]: option.value,
  };
}

async function setOptions(): Promise<void> {
  loading.value = true;

  options.value = await getOptions().then((options) => {
    return options.map((option) => ({
      title: t(option.title),
      value: option.value,
    }));
  });

  loading.value = false;
}

/////////////////////////////////////////////////////////////
////////////////////// HOOKS //////////////////////////////
/////////////////////////////////////////////////////////////
onMounted(async () => {
  void setOptions();

  // Listen for store updates
  const eventName = "store.update." + props.config.optionStore;
  window.removeEventListener(eventName, setOptions);
  window.addEventListener(eventName, setOptions);
});

onBeforeUnmount(() => {
  const eventName = "store.update." + props.config.optionStore;
  window.removeEventListener(eventName, setOptions);
});
</script>

<template>
  <AppSelect
    v-model="value"
    v-bind="{ ...config, size: undefined }"
    :label="config.label ? $t(config.label) : undefined"
    :disabled="disabled || config.disabled"
    :items="loading ? [] : options"
    :loading="loading"
  >
    <template v-if="config.multiple" v-slot:selection="{ item, index }">
      <v-chip v-if="index < 1">
        <span>{{ item.title }}</span>
      </v-chip>
      <span v-if="index === 1" class="text-grey text-caption align-self-center">
        (+{{ parseInt(value.length.toString()) - 1 }} {{ $t("others") }})
      </span>
    </template>
  </AppSelect>
</template>
